import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../app/Api/ApiSlice";
import Loader from "../components/Loader";

const Settings = () => {
    const [isLoading, setIsLoading] = useState(false);

    const [savingProfile, setSavingProfile] = useState(false);
    const [savingSecurity, setSavingSecurity] = useState(false);
    const [savingPrefs, setSavingPrefs] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [reactivating, setReactivating] = useState(false);


    // Profile
    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [country, setCountry] = useState("");

    // Security
    const [twoFA, setTwoFA] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");

    // Preferences
    const [privateAccount, setPrivateAccount] = useState(false);
    const [emailNotif, setEmailNotif] = useState(true);

    // Account status
    const [status, setStatus] = useState("active");
    const [deletionDate, setDeletionDate] = useState(null);
    const [password, setPassword] = useState("");

    // Fetch user
    useEffect(() => {
        const getUser = async () => {
            try {
                setIsLoading(true);
                const { data } = await api.get("/account/profile");

                setFirstname(data.firstname || "");
                setLastname(data.lastname || "");
                setEmail(data.email || "");
                setPhone(data.phone || "");
                setCountry(data.country || "");

                setTwoFA(data.user?.twoFA || false);
                setPrivateAccount(data.user?.privateAccount || false);
                setEmailNotif(data.user?.emailNotif || true);

                setStatus(data.user?.status || "active");
                setDeletionDate(data.user?.deletionScheduledAt || null);

            } catch (err) {
                toast.error("Failed to load settings");
            } finally {
                setIsLoading(false);
            }
        };

        getUser();
    }, []);

    // Profile update
    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        try {
            setSavingProfile(true);
            await api.patch("/account/profile", {
                firstname,
                lastname,
                email,
                phone,
                country
            });
            toast.success("Profile updated");
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setSavingProfile(false);
        }
    };

    // Security update
    const handleSecurityUpdate = async (e) => {
        e.preventDefault();
        try {
            setSavingSecurity(true);
            await api.patch("/account/profile/security", {
                oldPassword,
                newPassword,
                twoFA
            });
            toast.success("Security updated");
            setOldPassword("");
            setNewPassword("");
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setSavingSecurity(false);
        }
    };

    // Preferences update
    const handlePrefsUpdate = async (e) => {
        e.preventDefault();
        try {
            setSavingPrefs(true);
            await api.put("/profile/preferences", {
                privateAccount,
                emailNotif
            });
            toast.success("Preferences updated");
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setSavingPrefs(false);
        }
    };

    // Delete account
    const handleDelete = async () => {
        window.confirm("delete Account?")
        try {
            setDeleting(true);
            await api.patch("/account/profile/delete",{password});
            toast.success("Account scheduled for deletion");
            setStatus("pending_deletion");
        } catch (err) {
            console.log(err)
            toast.error("Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    // Reactivate account
    const handleReactivate = async () => {
        try {
            setReactivating(true);
            await api.post("/profile/reactivate");
            toast.success("Account reactivated");
            setStatus("active");
            setDeletionDate(null);
        } catch (err) {
            toast.error("Reactivation failed");
        } finally {
            setReactivating(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="settings-container">

            <header className="settings-header">
                <h1>Account Settings</h1>
                <p>Manage your profile, security, and account preferences.</p>
            </header>

            {/* Profile */}
            <section className="settings-card">
                <h3>Profile</h3>
                <form onSubmit={handleProfileUpdate} className="form-grid">
                    <div className="form-group">
                        <label>Firstname</label>
                        <input value={firstname} onChange={(e) => setFirstname(e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label>Lastname</label>
                        <input value={lastname} onChange={(e) => setLastname(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Phone</label>
                        <input value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label>Country</label>
                        <input value={country} onChange={(e) => setCountry(e.target.value)} />
                    </div>

                    <div className="form-group">
                        <label>Email</label>
                        <input value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>

                    <button className="btn-save" disabled={savingProfile}>
                        {savingProfile ? "Saving..." : "Update Profile"}
                    </button>
                </form>
            </section>

            {/* Security */}
            <section className="settings-card">
                <h3>Security</h3>
                <form onSubmit={handleSecurityUpdate} className="form-grid">
                    <div className="form-group">
                        <label>Old Password</label>
                        <input
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Two-Factor Authentication</label>
                        <input
                            type="checkbox"
                            checked={twoFA}
                            onChange={() => setTwoFA(!twoFA)}
                        />
                    </div>

                    <button className="btn-save" disabled={savingSecurity}>
                        {savingSecurity ? "Saving..." : "Update Security"}
                    </button>
                </form>
            </section>

            {/* Preferences */}
            <section className="settings-card">
                <h3>Preferences</h3>
                <form onSubmit={handlePrefsUpdate} className="form-grid">
                    <div className="form-group">
                        <label>Private Account</label>
                        <input
                            type="checkbox"
                            checked={privateAccount}
                            onChange={() => setPrivateAccount(!privateAccount)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Email Notifications</label>
                        <input
                            type="checkbox"
                            checked={emailNotif}
                            onChange={() => setEmailNotif(!emailNotif)}
                        />
                    </div>

                    <button className="btn-save" disabled={savingPrefs}>
                        {savingPrefs ? "Saving..." : "Update Preferences"}
                    </button>
                </form>
            </section>

            {/* Account */}
            <section className="settings-card">
                <h3>Delete Account</h3>

                {status === "pending_deletion" && (
                    <p className="warning-text">
                        Account scheduled for deletion {deletionDate && `on ${new Date(deletionDate).toLocaleDateString()}`}
                    </p>
                )}

                <div className="account-actions">
                    {status === "pending_deletion" && (
                        <button
                            className="btn-warning"
                            onClick={handleReactivate}
                            disabled={reactivating}
                        >
                            {reactivating ? "Restoring..." : "Reactivate Account"}
                        </button>
                    )}

                    <div className="form-group">
                        <label>Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    <button
                        className="profile-delete-btn"
                        onClick={handleDelete}
                        disabled={deleting || status === "pending_deletion"}
                    >
                        {deleting ? "Processing..." : "Delete Account"}
                    </button>
                </div>
            </section>

        </div>
    );
};

export default Settings;